/**
 * "Available today" panel — full-day-free cars and cars returning before 9 PM.
 * `sanitized` drops every client detail and click-through, so affiliate logins
 * can use the same panel on Car Availability.
 */
export default function TodayAvailabilityPanel({ today, onOpen, sanitized = false }) {
  const { free, returning } = today;
  const fmtTime = (d) => d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  const fmtNext = (d) => {
    const now = new Date();
    const today0 = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const days = Math.floor((new Date(d.getFullYear(), d.getMonth(), d.getDate()) - today0) / 86400000);
    const t = fmtTime(d);
    if (days === 0) return `Today ${t}`;
    if (days === 1) return `Tomorrow ${t}`;
    return `${d.toLocaleDateString(undefined, { weekday: "short" })} ${t}`;
  };
  const open = (id) => { if (!sanitized && id && onOpen) onOpen(id); };
  const guest = (b) => (sanitized ? "" : b.customer_name || "Guest");

  if (free.length === 0 && returning.length === 0) {
    return (
      <div className="border border-border bg-card p-5 mb-6" data-testid="today-availability-panel">
        <div className="font-heading text-lg">Available today</div>
        <div className="label-mono text-xs text-muted-foreground">Fleet status right now — full day free · returning before 9 PM</div>
        <div className="text-sm text-muted-foreground pt-4">Every car is booked out past 9 PM today.</div>
      </div>
    );
  }

  return (
    <div className="border border-border bg-card p-5 mb-6" data-testid="today-availability-panel">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <div className="font-heading text-lg">Available today</div>
          <div className="label-mono text-xs text-muted-foreground">
            {new Date().toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" })}
            {" · "}full day free · returning before 9 PM
          </div>
        </div>
        <div className="flex items-center gap-3 text-xs">
          <span className="label-mono text-emerald-500" data-testid="today-free-count">Free · {free.length}</span>
          <span className="label-mono text-amber-500" data-testid="today-returning-count">Returning · {returning.length}</span>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <div className="label-mono text-xs text-emerald-500 mb-2 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block" /> Fully available all day ({free.length})
          </div>
          {free.length === 0 ? (
            <div className="text-xs text-muted-foreground py-4">No cars are free the whole day.</div>
          ) : (
            <ul className="divide-y divide-border border border-border bg-background/40 max-h-72 overflow-y-auto" data-testid="today-free-list">
              {free.map(({ vehicle: v, next }) => (
                <li key={v.id} className="px-3 py-2 text-sm flex items-center justify-between gap-3" data-testid={`today-free-${v.id}`}>
                  <div className="min-w-0">
                    <div className="font-heading truncate">{v.make} {v.model}{v.year ? ` · ${v.year}` : ""}</div>
                    <div className="text-[11px] text-muted-foreground font-mono truncate">{v.license_plate || "—"}{v.color ? ` · ${v.color}` : ""}</div>
                  </div>
                  <div className="flex flex-col items-end gap-0.5 text-right whitespace-nowrap">
                    {next ? (
                      <>
                        <span className="label-mono text-[10px] text-emerald-500">FREE</span>
                        <span
                          className={`text-[11px] text-amber-500 ${sanitized ? "" : "cursor-pointer hover:underline"}`}
                          onClick={() => open(next.b.id)}
                          data-testid={`today-free-next-${v.id}`}
                          title={sanitized ? `Next pickup: ${next.start.toLocaleString()}` : `Next pickup: ${next.start.toLocaleString()} · ${guest(next.b)}`}
                        >
                          Out {fmtNext(next.start)}
                        </span>
                      </>
                    ) : (
                      <span className="label-mono text-[10px] text-emerald-500">FREE · 7d+</span>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div>
          <div className="label-mono text-xs text-amber-500 mb-2 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-amber-500 inline-block" /> Returning today before 9 PM ({returning.length})
          </div>
          {returning.length === 0 ? (
            <div className="text-xs text-muted-foreground py-4">No cars are returning before 9 PM.</div>
          ) : (
            <ul className="divide-y divide-border border border-border bg-background/40 max-h-72 overflow-y-auto" data-testid="today-returning-list">
              {returning.map(({ vehicle: v, booking: b, returnAt, next }) => (
                <li
                  key={v.id}
                  onClick={() => open(b.id)}
                  className={`px-3 py-2 text-sm flex items-center justify-between gap-3 ${sanitized ? "" : "cursor-pointer hover:bg-secondary/40 transition-colors"}`}
                  data-testid={`today-returning-${v.id}`}
                  title={sanitized ? `Returns ${returnAt.toLocaleString()}` : `Currently with ${guest(b)} · returns ${returnAt.toLocaleString()}`}
                >
                  <div className="min-w-0">
                    <div className="font-heading truncate">{v.make} {v.model}{v.year ? ` · ${v.year}` : ""}</div>
                    <div className="text-[11px] text-muted-foreground font-mono truncate">
                      {v.license_plate || "—"}{sanitized ? "" : ` · with ${guest(b)}`}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-0.5 text-right whitespace-nowrap">
                    <span className="label-mono text-amber-500 text-xs" data-testid={`today-return-time-${v.id}`}>
                      In {fmtTime(returnAt)}
                    </span>
                    {next && (
                      <span
                        className={`text-[11px] text-amber-500/80 ${sanitized ? "" : "hover:underline"}`}
                        data-testid={`today-returning-next-${v.id}`}
                        title={sanitized ? `Next pickup after return: ${next.start.toLocaleString()}` : `Next pickup after return: ${next.start.toLocaleString()} · ${guest(next.b)}`}
                        onClick={(e) => { e.stopPropagation(); open(next.b.id); }}
                      >
                        Out {fmtNext(next.start)}
                      </span>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
